import { ARENA, BOOST_ACCEL_MULTIPLIER, BOOST_SPEED_MULTIPLIER, computeStats, DEFAULT_UPGRADES, DEFAULT_GAME_CONFIG, arcadeImpact } from "@neon-arena/shared";
import type { InputState, VehicleBlueprint, CarStats, CarUpgrade, StatDamage, PhysicsConfig } from "@neon-arena/shared";
import type { ArenaObstacle } from "./PhysicsWorld.js";

export interface SelfCollisionEvent {
  type: "wall" | "obstacle";
  /** Index into the obstacles array for obstacle events; -1 for wall events. */
  index: number;
  approachSpeed: number;   // m/s, pre-response closing speed
  impactSeverity: number;  // 0–1, head-on fraction
  nx: number;              // outward normal from surface toward car
  nz: number;
  /** Stable cooldown key for VehicleDamageModel (includes "wall" or "ob{i}"). */
  tag: string;
}

export interface VehicleSnapshot {
  x: number;
  y: number;
  z: number;
  rotY: number;
  vx: number;
  vy: number;
  vz: number;
  speed: number;
  latSpeed: number;
}

export class VehicleController {
  x = 0;
  y = 0;
  z = 0;
  rotY = 0;

  private fwdSpeed = 0;  // m/s along heading
  private latSpeed = 0;  // m/s sideways (for drift)
  private blueprint: VehicleBlueprint;
  private obstacles: ArenaObstacle[];
  private arenaHalfSize: number;
  private carStats: CarStats;
  private currentUpgrades: CarUpgrade = DEFAULT_UPGRADES;
  private activePreset: string = "stock";
  private physicsConfig: PhysicsConfig;
  private debugLogTimer = 0;
  // Temporary effect multipliers (overdrive power-up). 1 = no effect.
  private effectSpeedMult = 1;
  private effectAccelMult = 1;

  constructor(
    obstacles: ArenaObstacle[],
    blueprint: VehicleBlueprint,
    spawn: { x: number; y: number; z: number; rotY: number },
    options?: { arenaSize?: number; stats?: CarStats; physics?: PhysicsConfig },
  ) {
    this.obstacles = obstacles;
    this.blueprint = blueprint;
    this.arenaHalfSize = (options?.arenaSize ?? ARENA.size) / 2;
    this.carStats = options?.stats ?? computeStats(blueprint);
    this.physicsConfig = options?.physics ?? DEFAULT_GAME_CONFIG.vehicle.physics;
    this.x = spawn.x;
    this.z = spawn.z;
    this.rotY = spawn.rotY;
  }

  setBlueprint(bp: VehicleBlueprint): void {
    this.blueprint = bp;
    this.carStats = computeStats(bp, this.currentUpgrades);
  }

  /** Replace the active stat set directly (e.g. after an upgrade purchase). */
  setStats(stats: CarStats): void {
    this.carStats = stats;
  }

  /**
   * Apply upgrade levels and recompute stats.
   * Stores upgrades so applyStatDamage can fold damage on top of the correct baseline.
   */
  applyUpgrades(upgrades: CarUpgrade): void {
    this.currentUpgrades = upgrades;
    this.carStats = computeStats(this.blueprint, upgrades);
  }

  setActivePreset(name: string): void { this.activePreset = name; }
  getActivePreset(): string { return this.activePreset; }

  /**
   * Set temporary top-speed / acceleration multipliers (overdrive power-up).
   * Applied on top of boost inside applyInput. Pass (1, 1) to clear.
   */
  setEffectMultipliers(speedMult: number, accelMult: number): void {
    this.effectSpeedMult = Number.isFinite(speedMult) && speedMult > 0 ? speedMult : 1;
    this.effectAccelMult = Number.isFinite(accelMult) && accelMult > 0 ? accelMult : 1;
  }

  getEffectMultipliers(): { speed: number; accel: number } {
    return { speed: this.effectSpeedMult, accel: this.effectAccelMult };
  }
  getGearShiftSpeed(): number { return this.carStats.gearShiftSpeed; }

  /**
   * Apply zone damage on top of the current upgrade-modified baseline.
   * Called when a stat-damage event is received from the server (future).
   */
  applyStatDamage(damage: StatDamage): void {
    this.carStats = computeStats(this.blueprint, this.currentUpgrades, damage);
  }

  // Called once per frame. Replaces the old Rapier-based applyInput.
  // Returns self-collision events (wall or obstacle impacts) that occurred this tick.
  applyInput(input: InputState, dt: number): SelfCollisionEvent[] {
    const bp = this.blueprint;
    const cs = this.carStats;
    const { throttle, brake, steer, handbrake, boost } = input;
    const { turnRate, traction, drift } = cs;
    const effMaxSpeed = (boost
      ? cs.topSpeed * (1 + (BOOST_SPEED_MULTIPLIER - 1) * cs.boostPower)
      : cs.topSpeed) * this.effectSpeedMult;
    const effAccel = (boost
      ? cs.accel * (1 + (BOOST_ACCEL_MULTIPLIER - 1) * cs.boostPower)
      : cs.accel) * this.effectAccelMult;
    const maxSpeed = effMaxSpeed;
    // effectiveAccel/Brake = stat × (referenceMass / mass). At reference mass the
    // factor is 1.0 so balanced-starter behaviour is preserved exactly.
    const massFactor   = this.physicsConfig.referenceMass / cs.mass;
    const acceleration = effAccel      * massFactor;
    const braking      = cs.brakeForce * massFactor;
    const maxRev = cs.topSpeed * 0.3;

    // ── Debug: log speed/config every 2 s while throttle is held ───
    this.debugLogTimer -= dt;
    if (this.debugLogTimer <= 0 && throttle > 0.02 && import.meta.env.DEV && typeof window !== "undefined") {
      this.debugLogTimer = 2;
      const speedRatioNow = Math.min(1, Math.abs(this.fwdSpeed) / maxSpeed);
      console.log("[speed-debug]", {
        preset:           this.activePreset,
        topSpeed:         cs.topSpeed.toFixed(1),   // from blueprint → computeStats
        effMaxSpeed:      maxSpeed.toFixed(1),       // topSpeed × boost multiplier
        currentSpeed:     this.fwdSpeed.toFixed(2),
        speedRatio:       speedRatioNow.toFixed(3),
        boost,
      });
    }

    // ── Throttle / power curve ──────────────────────────────────────
    // GTA 5-style: strong off the line, tapers toward top speed.
    if (throttle > 0.02) {
      const speedRatio = Math.max(0, this.fwdSpeed / maxSpeed);
      const grip = 1.0 - speedRatio * (1 - cs.downforce);
      this.fwdSpeed += throttle * acceleration * Math.max(0.4, grip) * dt;
      this.fwdSpeed = Math.min(this.fwdSpeed, maxSpeed);
    }

    // ── Braking / reverse ───────────────────────────────────────────
    if (brake > 0.02) {
      if (this.fwdSpeed > 0.3) {
        this.fwdSpeed -= brake * braking * dt;
        this.fwdSpeed = Math.max(0, this.fwdSpeed);
      } else {
        this.fwdSpeed -= brake * acceleration * this.physicsConfig.reverseAccelScale * dt;
        this.fwdSpeed = Math.max(-maxRev, this.fwdSpeed);
      }
    }

    // ── Engine braking / rolling friction ──────────────────────────
    if (throttle <= 0.02 && brake <= 0.02) {
      const friction = braking * this.physicsConfig.friction;
      if (this.fwdSpeed > 0) {
        this.fwdSpeed = Math.max(0, this.fwdSpeed - friction * dt);
      } else if (this.fwdSpeed < 0) {
        this.fwdSpeed = Math.min(0, this.fwdSpeed + friction * dt);
      }
    }

    // ── Steering ────────────────────────────────────────────────────
    // Speed builds authority: slow = 34%, max speed = 112% of turnRate.
    // Handbrake adds 35% turn boost for tighter power-slides.
    const absSpd = Math.abs(this.fwdSpeed);
    if (absSpd > 0.25) {
      const speedFactor = Math.min(1, absSpd / maxSpeed);
      const authority = turnRate * (this.physicsConfig.turnFactorLow + speedFactor * this.physicsConfig.turnFactorHigh);
      const dir = this.fwdSpeed >= 0 ? 1 : -1;
      const steerMult = handbrake ? this.physicsConfig.handbrakeTurnBoost : 1.0;
      this.rotY += steer * authority * steerMult * dir * dt;
    }

    // ── Lateral traction / handbrake drift ─────────────────────────
    if (handbrake && absSpd > 2) {
      // Handbrake: rear kicks out sideways (oversteer)
      const targetLat = steer * absSpd * drift * this.physicsConfig.driftTargetScale;
      this.latSpeed += (targetLat - this.latSpeed) * Math.min(1, dt * this.physicsConfig.driftLateralLerp);
      // Extra yaw when sliding — rear steps out
      this.rotY += steer * drift * this.physicsConfig.driftYawBoost * dt * Math.sign(this.fwdSpeed);
    }
    // Lateral grip recovery expressed as a proper exponential decay constant (1/s).
    // k = -ln(1 - gripFactor) × 30  →  identical to the old Math.pow form but robust
    // against numerical issues when gripFactor approaches 1.
    const gripFactor = handbrake ? traction * this.physicsConfig.tractionGripDrift : traction * this.physicsConfig.tractionGripFull;
    const kGrip = -Math.log(Math.max(1e-6, 1 - gripFactor)) * 30;
    this.latSpeed *= Math.exp(-kGrip * dt);

    // ── Integrate position ─────────────────────────────────────────
    const sinR = Math.sin(this.rotY);
    const cosR = Math.cos(this.rotY);
    this.x += (sinR * this.fwdSpeed + cosR * this.latSpeed) * dt;
    this.z += (cosR * this.fwdSpeed - sinR * this.latSpeed) * dt;
    this.y = 0;

    const selfEvents: SelfCollisionEvent[] = [];

    // ── Wall collision ──────────────────────────────────────────────
    // Operate in world-space velocity so the response is correct regardless of heading.
    // Local↔world: vx = fwd·sinR + lat·cosR,  vz = fwd·cosR − lat·sinR
    //              fwd = vx·sinR + vz·cosR,    lat = vx·cosR − vz·sinR
    const hw = this.arenaHalfSize - bp.body.length * 0.55;
    let vx = sinR * this.fwdSpeed + cosR * this.latSpeed;
    let vz = cosR * this.fwdSpeed - sinR * this.latSpeed;
    const wCOR = this.physicsConfig.wallRestitution;
    const wPen = this.physicsConfig.wallHeadOnPenalty;
    let hitWall = false;
    let wallScale = 1;
    const wGT = this.physicsConfig.wallGlancingThreshold;

    function recordWallHit(nx: number, nz: number): void {
      // Compute pre-response approach speed and severity.
      const approach = Math.max(0, -(vx * nx + vz * nz));
      if (approach > 0) {
        const relSpeed = Math.sqrt(vx * vx + vz * vz);
        const severity = relSpeed > 0.001 ? approach / relSpeed : 0;
        selfEvents.push({ type: "wall", index: -1, approachSpeed: approach, impactSeverity: severity, nx, nz, tag: "wall" });
      }
    }

    if (this.x < -hw) { this.x = -hw; recordWallHit( 1, 0); const r = arcadeImpact(vx, vz,  1, 0, wCOR, wPen, wGT); vx += r.dvx; vz += r.dvz; wallScale = Math.min(wallScale, r.fwdSpeedScale); hitWall = true; }
    if (this.x >  hw) { this.x =  hw; recordWallHit(-1, 0); const r = arcadeImpact(vx, vz, -1, 0, wCOR, wPen, wGT); vx += r.dvx; vz += r.dvz; wallScale = Math.min(wallScale, r.fwdSpeedScale); hitWall = true; }
    if (this.z < -hw) { this.z = -hw; recordWallHit(0,  1); const r = arcadeImpact(vx, vz, 0,  1, wCOR, wPen, wGT); vx += r.dvx; vz += r.dvz; wallScale = Math.min(wallScale, r.fwdSpeedScale); hitWall = true; }
    if (this.z >  hw) { this.z =  hw; recordWallHit(0, -1); const r = arcadeImpact(vx, vz, 0, -1, wCOR, wPen, wGT); vx += r.dvx; vz += r.dvz; wallScale = Math.min(wallScale, r.fwdSpeedScale); hitWall = true; }
    if (hitWall) {
      vx *= wallScale;
      vz *= wallScale;
      this.fwdSpeed = vx * sinR + vz * cosR;
      this.latSpeed = vx * cosR - vz * sinR;
    }

    // ── Obstacle collision ──────────────────────────────────────────
    // Push out with AABB, then reflect world-space velocity along the collision
    // normal — correct for any vehicle heading, not just axis-aligned hits.
    for (let i = 0; i < this.obstacles.length; i++) {
      const ob = this.obstacles[i];
      const dx = this.x - ob.x;
      const dz = this.z - ob.z;
      const ex = ob.hx + bp.body.width * 0.55;
      const ez = ob.hz + bp.body.length * 0.55;
      const px = ex - Math.abs(dx);
      const pz = ez - Math.abs(dz);
      if (px > 0 && pz > 0) {
        let nx = 0, nz = 0;
        if (px < pz) { this.x += Math.sign(dx) * px; nx = Math.sign(dx); }
        else         { this.z += Math.sign(dz) * pz; nz = Math.sign(dz); }
        // World-space velocity at moment of impact (pre-response).
        let wvx = sinR * this.fwdSpeed + cosR * this.latSpeed;
        let wvz = cosR * this.fwdSpeed - sinR * this.latSpeed;
        const dot = wvx * nx + wvz * nz;
        if (dot < 0) {
          // Record event from pre-response velocity.
          const approach = Math.max(0, -(wvx * nx + wvz * nz));
          const relSpeed = Math.sqrt(wvx * wvx + wvz * wvz);
          const severity = relSpeed > 0.001 ? approach / relSpeed : 0;
          selfEvents.push({ type: "obstacle", index: i, approachSpeed: approach, impactSeverity: severity, nx, nz, tag: `ob${i}` });

          // obstacleRestitution is used as (1+COR), so COR = obstacleRestitution − 1
          const r = arcadeImpact(wvx, wvz, nx, nz, this.physicsConfig.obstacleRestitution - 1, this.physicsConfig.obstacleHeadOnPenalty, this.physicsConfig.obstacleGlancingThreshold);
          wvx = (wvx + r.dvx) * r.fwdSpeedScale;
          wvz = (wvz + r.dvz) * r.fwdSpeedScale;
          // Guarantee a minimum outward speed so low-speed contacts don't feel sticky.
          const outward = wvx * nx + wvz * nz;
          const minBounce = this.physicsConfig.minimumObstacleBounceMps;
          if (outward < minBounce) {
            wvx += nx * (minBounce - outward);
            wvz += nz * (minBounce - outward);
          }
          this.fwdSpeed = wvx * sinR + wvz * cosR;
          this.latSpeed = wvx * cosR - wvz * sinR;
        }
      }
    }

    return selfEvents;
  }

  /**
   * Resolve overlaps and apply velocity response for car-car collisions.
   * Call once per frame after applyInput(), before snapshot().
   * `others` are the visual positions of remote cars — alive only.
   * Returns one event per car that was actually hit (approach speed > 0).
   */
  applyCarCollisions(others: Array<{ id?: string; x: number; z: number; hx: number; hz: number }>): Array<{ id?: string; approachSpeed: number; nx: number; nz: number }> {
    const bp = this.blueprint;
    const sinR = Math.sin(this.rotY);
    const cosR = Math.cos(this.rotY);
    const cfg = this.physicsConfig;
    const events: Array<{ id?: string; approachSpeed: number; nx: number; nz: number }> = [];

    for (const other of others) {
      const dx = this.x - other.x;
      const dz = this.z - other.z;
      const ex = other.hx + bp.body.width * 0.55;
      const ez = other.hz + bp.body.length * 0.55;
      const px = ex - Math.abs(dx);
      const pz = ez - Math.abs(dz);
      if (px > 0 && pz > 0) {
        let nx = 0, nz = 0;
        if (px < pz) { this.x += Math.sign(dx) * px; nx = Math.sign(dx); }
        else         { this.z += Math.sign(dz) * pz; nz = Math.sign(dz); }
        const wvx = sinR * this.fwdSpeed + cosR * this.latSpeed;
        const wvz = cosR * this.fwdSpeed - sinR * this.latSpeed;
        if (wvx * nx + wvz * nz < 0) {
          // Record approach speed before applying the velocity response
          const approachSpeed = -(wvx * nx + wvz * nz);
          const r = arcadeImpact(wvx, wvz, nx, nz, cfg.carCarRestitution - 1, cfg.carCarHeadOnPenalty, cfg.carCarGlancingThreshold);
          const nvx = (wvx + r.dvx) * r.fwdSpeedScale;
          const nvz = (wvz + r.dvz) * r.fwdSpeedScale;
          this.fwdSpeed = nvx * sinR + nvz * cosR;
          this.latSpeed = nvx * cosR - nvz * sinR;
          events.push({ id: other.id, approachSpeed, nx, nz });
        }
      }
    }
    return events;
  }

  teleport(x: number, _y: number, z: number, rotY: number): void {
    this.x = x;
    this.y = 0;
    this.z = z;
    this.rotY = rotY;
    this.fwdSpeed = 0;
    this.latSpeed = 0;
  }

  snapshot(): VehicleSnapshot {
    const sinR = Math.sin(this.rotY);
    const cosR = Math.cos(this.rotY);
    return {
      x: this.x,
      y: this.y,
      z: this.z,
      rotY: this.rotY,
      vx: sinR * this.fwdSpeed,
      vy: 0,
      vz: cosR * this.fwdSpeed,
      speed: this.fwdSpeed,
      latSpeed: this.latSpeed,
    };
  }
}
